import React, { useState } from 'react';
import {
  Button,
  Gallery, Grid, Modal,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import { Image, ImageProps } from '@unpic/react';
import { messages } from '../ProductGallery';
import { Review } from '../../../../helpers/reviewHelper';
import { ReviewStars } from '../../../review-stars/ReviewStars';
import { TimeAgo } from './TimeAgo';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';

type UserImagesGalleryProps = {
  category?: string;
  productName?: string;
  userImages: string[];
  crawlerId: string;
  title: string;
  reviews?: Review[];
  singleReview?: Review;
};

type DataLayerPayload = {
  category: string;
  action: string;
  reviewId?: string;
  title?: string;
};

const MAX_VISIBLE = 4;
const THUMBNAIL_WIDTHS = [48, 92];
const IMAGE_WIDTHS = [150, 260, 450, 740];

const resizeImageUri = (
  imageUri: string,
  widths: number[],
  layout: 'fullWidth' | 'constrained' = 'constrained',
) => {
  const url = new URL(imageUri);
  const pixelRatio = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio, 2) : 1; // only max 2 supported
  const scaledWidths = widths.map((w) => Math.round(w * pixelRatio));
  url.searchParams.set('width', scaledWidths[0].toString());
  url.searchParams.set('height', scaledWidths[0].toString());

  const imageProps = {
    src: url.toString(),
    srcSet: scaledWidths.map((w) => {
      url.searchParams.set('width', w.toString());
      url.searchParams.set('height', w.toString());
      return `${url.toString()} ${w}w`;
    }).join(', '),
    sizes: `(min-width: ${scaledWidths[scaledWidths.length - 1]}px) ${scaledWidths[scaledWidths.length - 1]}px, 100vw`,
    layout,
  };

  return layout === 'fullWidth'
    ? imageProps as ImageProps
    : { ...imageProps, width: scaledWidths[0], height: scaledWidths[0] } as ImageProps;
};

const UserImagesGallery = ({
  category,
  productName,
  reviews,
  singleReview,
  userImages,
  crawlerId,
  title,
}: UserImagesGalleryProps) => {
  const intl = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(1);
  const filteredImages = userImages.filter(Boolean);

  const getCommonPayload = (action: string): DataLayerPayload => ({
    ...(singleReview ? { reviewId: singleReview.id } : { title }),
    category: category || '',
    action,
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
    dispatchDataLayerEntry(
      productName || '',
      'review_gallery_open',
      getCommonPayload('thumbnail_click'),
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dispatchDataLayerEntry(
      productName || '',
      'review_gallery_close',
      getCommonPayload('modal_close_x'),
    );
  };

  const handleNavigateModal = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
    const direction = currentTarget.className.includes('prev') ? 'prev' : 'next';
    dispatchDataLayerEntry(
      productName || '',
      'navigate_review_gallery',
      getCommonPayload(`modal_navigate.${direction}_click`),
    );
  };

  const thumbnailAlt = intl.formatMessage({
    id: 'productDetail.reviews.customerImages.thumbnailAlt', defaultMessage: 'Customer uploaded review example thumbnail',
  });
  const imageAlt = intl.formatMessage({
    id: 'productDetail.reviews.customerImages.imageAlt', defaultMessage: 'Enlarged customer uploaded review example',
  });

  return (
    <div id={crawlerId}>
      <Gallery
        id={`${crawlerId}-gallery-nav`}
        defaultActiveItem={currentItem || currentItem}
        onChangeActiveItem={(activeItem) => setCurrentItem(activeItem)}
        prevButton={(
          <Gallery.Button
            variant="prev"
            screenReaderLabel={intl.formatMessage(messages.prev)}
            className="pd-hidden"
          />
        )}
        nextButton={(
          <Gallery.Button
            variant="next"
            screenReaderLabel={intl.formatMessage(messages.next)}
            className="pd-hidden"
          />
        )}
      >
        <Gallery.Root className="pd-hidden">
          {filteredImages.map((uri, index) => (
            <Gallery.Item key={`image-${uri + index}`} itemNumber={index + 1} />
          ))}
        </Gallery.Root>
        <Gallery.Navigation maxItems={MAX_VISIBLE}>
          {filteredImages.map((uri, index) => (
            <Gallery.Navigation.Item
              key={`thumbnail-image-${uri + index}`}
              itemNumber={index + 1}
              onClick={() => handleOpenModal()}
            >
              <Image
                {...resizeImageUri(uri, THUMBNAIL_WIDTHS, 'fullWidth')}
                className="pd-cover-parent"
                loading={index < MAX_VISIBLE ? 'eager' : 'lazy'}
                alt={thumbnailAlt}
              />
            </Gallery.Navigation.Item>
          ))}
        </Gallery.Navigation>
      </Gallery>
      <Modal
        size={singleReview ? 'md' : 'xl'}
        isOpen={isModalOpen}
        title={title}
        onClose={() => handleCloseModal()}
        dismissLabel={intl.formatMessage(messages.close)}
      >
        <Gallery
          id={`${crawlerId}-gallery-modal`}
          defaultActiveItem={currentItem}
          prevButton={(
            <Gallery.Button
              variant="prev"
              screenReaderLabel={intl.formatMessage(messages.prev)}
              className={clsx({ 'pd-hidden': userImages.length === 1 })}
              onClick={handleNavigateModal}
            />
          )}
          nextButton={(
            <Gallery.Button
              variant="next"
              screenReaderLabel={intl.formatMessage(messages.next)}
              className={clsx({ 'pd-hidden': userImages.length === 1 })}
              onClick={handleNavigateModal}
            />
          )}
        >
          <>
            <Modal.Content>
              {
                reviews ? (
                  <Gallery.Root>
                    {reviews.map((review, index) => (
                      <Gallery.Item
                        className="pd-aspect-auto"
                        key={`modal-image-${review.photos[0] + index}`}
                        itemNumber={index + 1}
                      >
                        <Grid className="pd-grid-cols-2 pd-gap-4">
                          <Grid.Item className="pd-col-start-1">
                            <Image
                              {...resizeImageUri(review.photos[0], IMAGE_WIDTHS, 'fullWidth')}
                              loading="lazy"
                              className="pd-rounded-md"
                              aspectRatio={1}
                              alt={imageAlt}
                            />
                          </Grid.Item>
                          <Grid.Item className="pd-col-start-2">
                            <Grid className="pd-grid-cols-1 pd-gap-4 pd-text-sm">
                              {(review.title || review.product_name) && (
                                <Grid.Item className="pd-font-bold">{review.title || review.product_name}</Grid.Item>
                              )}
                              <Grid.Item className="pd-flex pd-items-center">
                                <ReviewStars score={review.rating * 2} className="pd-mr-2" />
                                {review.rating}/5
                              </Grid.Item>
                              <Grid.Item>
                                {review.author.name}
                                {review.author.location && ` / ${review.author.location}`}{' '}
                                <TimeAgo date={review.date_created} />
                              </Grid.Item>
                              <Grid.Item>
                                <p className="pd-whitespace-pre-wrap">{review.comments}</p>
                              </Grid.Item>
                            </Grid>
                          </Grid.Item>
                        </Grid>
                      </Gallery.Item>
                    ))}
                  </Gallery.Root>
                ) : (
                  <Gallery.Root>
                    {filteredImages.map((uri, index) => (
                      <Gallery.Item key={`modal-image-${uri + index}`} itemNumber={index + 1}>
                        <Image
                          {...resizeImageUri(uri, IMAGE_WIDTHS, 'fullWidth')}
                          loading="lazy"
                          className="pd-cover-parent"
                          alt={imageAlt}
                        />
                      </Gallery.Item>
                    ))}
                  </Gallery.Root>
                )
              }
            </Modal.Content>
            <Modal.Footer className="pd-flex pd-flex-row-reverse">
              <Button iconBefore="arrow-right" onClick={handleCloseModal}>
                <FormattedMessage id="generic.modal.close" defaultMessage="Close" />
              </Button>
            </Modal.Footer>
          </>
        </Gallery>
      </Modal>
    </div>
  );
};

export default UserImagesGallery;
